import { resolveImagePath } from '@/utils/imageMap.ts';
import type { HeroData } from '@/data/types.ts';

interface HeroProps {
  data: HeroData;
}

export function Hero({ data }: HeroProps) {
  return (
    <section className="relative">
      {/* Main Hero Section with Background Image */}
      <div className="relative h-80 lg:h-[450px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${resolveImagePath(data.heroImage)})`,
            backgroundPosition: 'center 35%'
          }}
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-45" />
        
        {/* Hero Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 text-shadow-lg tracking-wide">
              {data.title}
            </h1>
            <p className="text-base md:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed font-light">
              {data.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Content Section Below Hero */}
      <div className="bg-white py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 lg:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left Content */}
            <div className="lg:pr-4">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6 leading-tight">
                {data.contentSection?.title}
              </h2>
              <div className="text-gray-700 leading-relaxed">
                <p className="text-base lg:text-lg mb-4">
                  <strong className="text-gray-900">{data.contentSection?.highlight}</strong>{' '}
                  {data.contentSection?.description}
                </p>
                {data.contentSection?.readMoreLink && (
                  <p>
                    <a 
                      href={data.contentSection.readMoreLink}
                      className="text-red-600 hover:text-red-700 font-semibold text-sm lg:text-base"
                    >
                      Read More
                    </a>
                  </p>
                )}
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:pl-4">
              {data.contentSection?.image && (
                <img
                  src={resolveImagePath(data.contentSection.image)}
                  alt={data.contentSection?.imageAlt || 'Good News Garage Staff'}
                  className="w-full h-auto rounded-md shadow-lg"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}